import {
  chainCommands,
  createParagraphNear,
  deleteSelection,
  joinBackward,
  liftEmptyBlock,
  selectNodeBackward,
  splitBlockAs,
} from "prosemirror-commands";
import { type Command, Selection } from "prosemirror-state";

import type { SchemaNodes } from "./adapter/schema";
import { schema } from "./adapter/schema";
import { getSharedAttrs } from "./utils";

const continuingBlockTypes: SchemaNodes[] = ["bullet_list_item", "ordered_list_item", "quote"];
const exitableBlockTypes: SchemaNodes[] = [
  "bullet_list_item",
  "ordered_list_item",
  "quote",
  "code_block",
];

const splitBlockKeepSharedAttrs = splitBlockAs((node, atEnd) => {
  const splitInHeading = node.type === schema.nodes.heading && !atEnd;
  const keepType =
    splitInHeading ||
    (node.content.size > 0 && continuingBlockTypes.includes(node.type.name as SchemaNodes));
  return {
    type: keepType ? node.type : schema.nodes.paragraph,
    attrs: splitInHeading
      ? { ...getSharedAttrs(node), level: node.attrs.level }
      : getSharedAttrs(node),
  };
});

/**
 * In a code block: inserts a newline, unless the cursor is preceded by two
 * trailing empty lines (\n\n). In that case, removes the empty lines and
 * creates a paragraph after the code block (exiting the code block).
 */
const newlineOrExitCode: Command = (state, dispatch) => {
  const { $head, $anchor } = state.selection;
  if ($head.parent.type !== schema.nodes.code_block || !$head.sameParent($anchor)) return false;

  const textBefore = $head.parent.textBetween(0, $head.parentOffset);
  const textAfter = $head.parent.textBetween($head.parentOffset, $head.parent.content.size);

  if (textBefore.endsWith("\n\n") && textAfter.length === 0) {
    if (dispatch) {
      const tr = state.tr;
      // Remove the two trailing newlines
      tr.delete($head.pos - 2, $head.pos);
      // Insert a paragraph after the code block
      const afterBlock = $head.after();
      const mapped = tr.mapping.map(afterBlock);
      tr.replaceWith(
        mapped,
        mapped,
        schema.nodes.paragraph.createAndFill(getSharedAttrs($head.parent))!,
      );
      tr.setSelection(Selection.near(tr.doc.resolve(mapped + 1)));
      dispatch(tr.scrollIntoView());
    }
    return true;
  }

  if (dispatch) dispatch(state.tr.insertText("\n").scrollIntoView());
  return true;
};

const exitEmptyBlock: Command = (state, dispatch) => {
  const { $head, $anchor } = state.selection;
  if (
    !continuingBlockTypes.includes($head.parent.type.name as SchemaNodes) ||
    !$head.sameParent($anchor) ||
    $head.parent.content.size > 0
  )
    return false;

  if (dispatch) {
    dispatch(
      state.tr
        .setBlockType($head.pos, $head.pos, schema.nodes.paragraph, getSharedAttrs($head.parent))
        .scrollIntoView(),
    );
  }

  return true;
};

/** Enter key handler: inserts newlines in code blocks, exits empty blocks, splits or continues block types. */
export const enterCommand = chainCommands(
  newlineOrExitCode,
  createParagraphNear,
  liftEmptyBlock,
  exitEmptyBlock,
  splitBlockKeepSharedAttrs,
);

const replaceDashOrAsteriskWithBulletList: Command = (state, dispatch) => {
  const { $from, from, $to, to, empty } = state.selection;
  if (!empty || $from.parent.type.name !== "paragraph") {
    return false;
  }
  const textBefore = $from.parent.textBetween(0, $to.parentOffset);
  if (!["-", "*"].includes(textBefore)) {
    return false;
  }
  if (dispatch) {
    const tr = state.tr;
    tr.setBlockType(from, to, state.schema.nodes.bullet_list_item, getSharedAttrs($from.parent));
    // delete the dash/asterisk
    tr.delete(from - 1, from);
    dispatch(tr.scrollIntoView());
  }
  return true;
};

const replaceOneWithOrderedList: Command = (state, dispatch) => {
  const { $from, from, $to, to, empty } = state.selection;
  if (!empty || $from.parent.type.name !== "paragraph") {
    return false;
  }
  const textBefore = $from.parent.textBetween(0, $to.parentOffset);
  if (textBefore !== "1.") {
    return false;
  }
  if (dispatch) {
    const tr = state.tr;
    tr.setBlockType(from, to, state.schema.nodes.ordered_list_item, getSharedAttrs($from.parent));
    // delete the "1."
    tr.delete(from - 2, from);
    dispatch(tr.scrollIntoView());
  }
  return true;
};

/** Space key handler: converts markdown-style shortcuts (`-`, `*`, `1.`) into list blocks. */
export const spaceCommand = chainCommands(
  replaceDashOrAsteriskWithBulletList,
  replaceOneWithOrderedList,
);

const exitBlockAtStart: Command = (state, dispatch) => {
  const { $from, from, to, empty } = state.selection;
  if (
    !empty ||
    $from.parentOffset !== 0 ||
    !exitableBlockTypes.includes($from.parent.type.name as SchemaNodes)
  ) {
    return false;
  }
  if (dispatch) {
    const tr = state.tr;
    tr.setBlockType(from, to, state.schema.nodes.paragraph, getSharedAttrs($from.parent));
    dispatch(tr.scrollIntoView());
  }
  return true;
};

/** Backspace key handler: exits empty special blocks (list items, quote, code block) back to paragraph. */
export const backspaceCommand = chainCommands(
  deleteSelection,
  joinBackward,
  selectNodeBackward,
  exitBlockAtStart,
);
